'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '../../types/dynamic-data';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import styles from './hero-layout.module.css';

export function Hero({ slides }: { slides: TDynamicData[] }) {
  const options = {
    align: 'center' as const,
    containScroll: false as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section className="w-full gap-8 bg-[#F3F4F6] pb-8 pt-4 sm:pb-10 sm:pt-6 md:pb-14 lg:pb-[72px] lg:pt-8">
      <Carousel
        options={options}
        enableLoop
        className={{
          wrapper: 'w-full',
          main: 'w-full',
          container: cn('w-full', styles.initialTrack),
          slide: cn(
            styles.slide,
            'w-full overflow-hidden rounded-2xl',
          ),
        }}
        slides={slides.map((item, index) => (
          <HeroSlide item={item} key={item.name} index={index} />
        ))}
      />
      <div
        className={cn(
          'mt-4 flex items-center justify-center gap-2 sm:mt-6 lg:mt-8',
          'ea-carousel__dots__container',
        )}
      >
        {slides.map((_, index) => (
          <div
            key={index}
            className={cn(
              'flex h-2 w-2 items-center justify-start rounded-full bg-[#E5E7EB] transition-all',
              {
                'w-10': selectedIndex === index,
              },
            )}
          >
            {selectedIndex === index && (
              <div className="h-full w-[19px] rounded-full bg-[#000000] transition-all" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function HeroSlide({ item, index }: { item: TDynamicData; index: number }) {
  return (
    <div className="relative flex h-[500px] w-full items-end justify-start px-4 py-6 sm:h-[550px] sm:p-8 md:h-[575px] md:p-12 lg:h-[660px] lg:p-16 xl:h-[660px] xl:p-24">
      <div className="relative z-10 flex flex-col items-start justify-start gap-6 sm:gap-8">
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <p className="text-sm uppercase text-white">{item.fields[0]?.value}</p>
          <h1 className="w-full max-w-[690px] text-balance text-[40px] font-medium leading-[1.1] tracking-tighter text-white sm:text-5xl sm:leading-[1.1] lg:text-[56px] lg:leading-[1.1]">
            {item.name}
          </h1>
        </div>
        <Button asChild variant={'white'}>
          <Link prefetch={false} href={item.fields[3]?.value}>
            {item.fields[2]?.value} <HiOutlineArrowRightCircle size={24} />
          </Link>
        </Button>
      </div>
      <div className="absolute inset-0 z-[1] h-full w-full bg-black/20" />
      <Image
        src={item?.image?.url}
        alt={resolveImageAlt(item?.image, item?.name)}
        width={0}
        height={0}
        className="absolute inset-0 h-full w-full object-cover object-center"
        sizes="(min-width: 1777px) 1599px, (min-width: 640px) 90vw, 80vw"
        loading={index === 0 ? 'eager' : undefined}
        fetchPriority={index === 0 ? 'high' : undefined}
      />
    </div>
  );
}
